/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useState } from 'react';
import { RootState } from '@store/store';
import { useSelector } from 'react-redux';
import { Dialog, DialogContent, Link } from '@mui/material';

import FaIcon from '@components/FaIcon';
import MDBox from '@components/MDBox';
import MDButton from '@components/MDButton';
import MDTypography from '@components/MDTypography';
import { PlanError } from '@store/AppSlice';
import brandLogo from '@assets/images/ds-payment-image-2.jpg';
import breakpoints from '@theme/base/breakpoints';
import { useParams } from 'react-router-dom';
import { sendAmplitudeData } from '@utils/amplitude';
import {
  CLICKED_UPGRADE_PLAN,
  SHOWED_PAYMENT_REQUIRED
} from '@constants/eventNames';
import reactStringReplace from 'react-string-replace';
import { CloseButton } from '@components';

const PlanUpgradeModal = (props: {
  open: boolean;
  planError: PlanError | null;
  onClose: () => void;
}) => {
  const { open, planError, onClose } = props;

  const { projectId, chartId } = useParams();

  const [isMobileScreen, setIsMobileScreen] = useState(false);

  const userId = useSelector(
    (state: RootState) => state?.userDetails?.user?.id
  );
  const currentPlan = useSelector(
    (state: RootState) => state?.userDetails?.plan
  );

  const hasUsedTrial = useSelector(
    (state: RootState) => state?.userDetails?.has_used_trial
  );

  const isTrial = useSelector(
    (state: RootState) => state?.userDetails?.is_trial
  );
  const projectBoard = useSelector((state: RootState) => state?.projectBoard);

  const planUpgradeUrl = `${process.env.REACT_APP_PLAN_UPGRADE_URL}`;

  useEffect(
    () => setIsMobileScreen(window.innerWidth < breakpoints.values.lg),
    []
  );

  const dataUpdateFeatureNames = [
    'Max Monthly Data Update Limit',
    'Max Monthly Data Update Trial Limit'
  ];

  useEffect(() => {
    if (planError?.feature_name)
      sendAmplitudeData(SHOWED_PAYMENT_REQUIRED, {
        current_plan: `${currentPlan} ${isTrial ? '_TRIAL' : ''}`,
        project_id:
          chartId || projectId ? projectBoard?.currentProjectData?.id : '',
        feature_name: dataUpdateFeatureNames.includes(
          planError?.feature_name as any
        )
          ? 'Update Data'
          : planError?.feature_name,
        modal_name:
          planError?.modal_name === 'Upload Limit exceeded'
            ? 'Upload Limit'
            : planError?.modal_name,
        is_trial_utilised: hasUsedTrial ? 'yes' : 'no'
      });
  }, [planError?.feature_name]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      sx={{ zIndex: 6000 }}
      PaperProps={{
        sx: {
          width: isMobileScreen ? '40rem' : '45%',
          height: 'fit-content',
          maxWidth: isMobileScreen ? '40rem' : '45%',
          overflow: 'visible'
        }
      }}
    >
      <>
        <CloseButton onClose={() => onClose()} />
        <DialogContent
          sx={{
            p: 0,
            display: 'flex'
          }}
        >
          <MDBox
            display='flex'
            flexDirection='column'
            position='relative'
            width='100%'
            overflow='hidden'
            borderRadius='8px'
          >
            <MDBox display='flex' flexDirection='column' height='100%'>
              <MDBox
                flexBasis={isMobileScreen ? '100%' : '47%'}
                display='flex'
                flexDirection='column'
                alignItems='start'
                paddingY='20px'
                paddingX='35px'
              >
                <MDBox display='flex' flexDirection='column' alignItems='start'>
                  <MDBox display='flex' direction='row' alignItems='baseline'>
                    <MDTypography variant='h3' color='secondary' textGradient>
                      {planError?.title || ''}
                    </MDTypography>
                  </MDBox>
                </MDBox>
                <MDBox
                  display='flex'
                  flexDirection='row'
                  my={3}
                  justifyContent='space-between'
                  justifyItems='space-between'
                  sx={{ width: '100%' }}
                >
                  <MDBox width='55%' display='flex'>
                    <MDTypography variant='h5' color='info'>
                      {reactStringReplace(planError?.sub_title, '\n', () => (
                        <>
                          <br />
                          <br />
                        </>
                      ))}
                    </MDTypography>
                  </MDBox>
                  {!isMobileScreen && (
                    <MDBox
                      sx={{
                        display: 'flex',
                        height: '13rem',
                        width: '40%',
                        backgroundImage: `url(${brandLogo})`,
                        backgroundRepeat: 'no-repeat',
                        backgroundSize: '100% 100%'
                      }}
                    ></MDBox>
                  )}
                </MDBox>

                <MDBox
                  width='100%'
                  display='flex'
                  flexDirection='column'
                  alignItems='center'
                >
                  <MDButton
                    size='medium'
                    color='primary'
                    sx={{
                      width: '100%',
                      minHeight: '60px',
                      alignSelf: 'center'
                    }}
                    onClick={() => {
                      sendAmplitudeData(CLICKED_UPGRADE_PLAN, {
                        current_plan: `${currentPlan} ${
                          isTrial ? '_TRIAL' : ''
                        }`,
                        is_trial_utilised: hasUsedTrial ? 'yes' : 'no',
                        project_id: projectId
                      });
                      window.location.replace(
                        `${planUpgradeUrl}?userid=${userId}&plan_level=PRO${
                          projectId ? `&project_id=${projectId}` : ''
                        }`
                      );
                    }}
                  >
                    <MDTypography fontSize='1.25rem' color='light'>
                      {planError?.button_text || 'Upgrade or Free Trial'}
                    </MDTypography>
                  </MDButton>

                  <MDBox display='flex' flexDirection='row' gap={4} mt={2}>
                    {/* <MDBox
                      display='flex'
                      flexDirection='row'
                      alignItems='center'
                      gap={1}
                    >
                      <FaIcon icon='check' style={{ color: 'green' }} />
                      <MDTypography variant='h5' color='info'>
                        No credit card required
                      </MDTypography>
                    </MDBox> */}
                    <MDBox
                      display='flex'
                      flexDirection='row'
                      alignItems='center'
                      gap={1}
                    >
                      <FaIcon icon='check' style={{ color: 'green' }} />
                      <MDTypography variant='h5' color='info'>
                        GDPR Compliant
                      </MDTypography>
                    </MDBox>
                    <MDBox
                      display='flex'
                      flexDirection='row'
                      alignItems='center'
                      gap={1}
                    >
                      <FaIcon icon='check' style={{ color: 'green' }} />
                      <MDTypography variant='h5' color='info'>
                        Your data is never uploaded to ChatGPT
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </MDBox>
                <MDBox
                  display='flex'
                  justifyContent='center'
                  width='100%'
                  mt={1}
                >
                  <MDTypography
                    sx={{
                      cursor: 'pointer',
                      textAlign: 'center'
                    }}
                    variant='sm'
                    color='secondary'
                    component={Link}
                    underline
                    href={'https://datasquirrel.ai/pricing'}
                    target='_blank'
                    fontWeight='medium'
                    textGradient
                  >
                    Know more about DataSquirrel plans
                  </MDTypography>
                </MDBox>
              </MDBox>
            </MDBox>
          </MDBox>
        </DialogContent>
      </>
    </Dialog>
  );
};

export default PlanUpgradeModal;
